import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./CompetitorCompanies.module.scss";
import { CaretDownIcon, CheckIcon, EntryPointsIcon, TagIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { useState } from "react";
import clsx from "clsx";
import { UnlockIcon } from "@/frontend/shared/assets/icons/UnlockIcon";
import { SectionContentWrapper } from "@/frontend/widgets/SectionContentWrapper/SectionContentWrapper";
import { AddCompaniesTagsModal } from "@/frontend/components/AddCompaniesTagsModal/ui/AddCompaniesTagsModal";
import { createPortfolioCompanies } from "@/frontend/pages/CompaniesPage/model/services/createPortfolioCompanies";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TableLabels } from "@/frontend/shared/ui/Table/ui/TableLabels";
import { TableBody } from "@/frontend/shared/ui/Table/ui/TableBody/TableBody";
import { Competitor, EAccessDataStatus } from "@/frontend/features/Company/model/types/types";
import { NoAiDataContainer } from "@/frontend/features/Company/ui/NoAiDataContainer/NoAiDataContainer";
import { summaryMockDescription } from "../../../const/mock";
import { SectionLoader } from "../../Section/SectionLoader/SectionLoader";
import { removeCompetitorsFromPortfolioCompany } from "@/frontend/features/Company/Profile/model/services/removeCompetitorsFromPortfolioCompany";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import { getCompanyIdFromUrl } from "@/frontend/shared/lib/getCompanyIdFromUrl/getCompanyIdFromUrl";
import { AddCompaniesToPipelineModal } from "@/frontend/components/AddCompaniesToPipelineModal/AddCompaniesToPipelineModal";

export type TCompetitorCompaniesProps = {
  companyName?: string | null;
  competitors?: Competitor[];
  isLoadingCompetitors: boolean;
  refetchCompetitors: () => void;
  dataStatusFromAccess: EAccessDataStatus;
  onConfirmRequestDataAccess?: () => void;
  isLoadingRequestAccess?: boolean;
  logoUrl?: string | null;
  isInvestor?: boolean;
  onEdit?: () => void;
};

export function CompetitorCompanies({
  companyName,
  dataStatusFromAccess,
  refetchCompetitors,
  onConfirmRequestDataAccess,
  isLoadingRequestAccess,
  logoUrl,
  isLoadingCompetitors: isLoading,
  competitors,
  isInvestor,
  onEdit
}: TCompetitorCompaniesProps) {
  const [isCompetitorsShown, setIsCompetitorsShown] = useState(false);
  const [selectedAiRowIds, setSelectedAiRowIds] = useState<number[]>([]);
  const [shownModal, setShownModal] = useState<"tags" | "addToPipeline" | null>(null);
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState(false);

  const canDeleteCompetitors =
    dataStatusFromAccess !== EAccessDataStatus.FULL_DATA && dataStatusFromAccess !== EAccessDataStatus.PROFILE_DATA;
  const competitorsContent = () => {
    if (!competitors) return null;

    function handleAiCardChange(id: number) {
      setSelectedAiRowIds((currentSelected) => {
        if (!currentSelected.includes(id)) {
          return [...currentSelected, id];
        }
        return currentSelected.filter((cardId) => cardId !== id);
      });
    }

    function selectAllAiCompanies() {
      if (competitors?.length === selectedAiRowIds.length) return setSelectedAiRowIds([]);
      setSelectedAiRowIds(competitors?.map((item: { id: number }) => item.id) || []);
    }

    async function removeCompetitors() {
      const portfolioCompanyId = getCompanyIdFromUrl({ url: window.location.href });
      try {
        const res = await removeCompetitorsFromPortfolioCompany({
          competitorsIds: selectedAiRowIds,
          portfolioCompanyId
        });
        snackbarConstructor("Competitors removed successfully", "normal");
        setIsOpenDeleteConfirmation(false);
        refetchCompetitors();
      } catch (error) {
        const axiosError = error as AxiosError<{ message: string }>;
        const errorMessage = axiosError.response?.data?.message || "Error removing competitors";
        snackbarConstructor(errorMessage, "error");
        reportErrorToSentry(
          axiosError,
          `PORTFOLIO COMPANY REMOVE COMPETITORS MODAL: /portfolio-companies/${portfolioCompanyId}/competitors/remove/bulk`
        );
      }
    }

    const shownCompetitorsData = isCompetitorsShown ? competitors : competitors.slice(0, 5);
    const tableData = shownCompetitorsData?.map((item) => ({
      ...item,
      investors: []
    }));
    return (
      <>
        {isInvestor && (
          <div className={styles.innoveraBlockTitle}>
            <div className={styles.contentActionButtonsBlock}>
              {!!selectedAiRowIds.length && (
                <div className={styles.checkboxContainer}>
                  <Typography color="greyscale900" variant="label-4">
                    {selectedAiRowIds.length} Selected
                  </Typography>
                </div>
              )}
              <NormalButton
                size="large"
                variant="secondary"
                className={styles.actionButton}
                onClick={() => setShownModal("addToPipeline")}
                isDisabled={selectedAiRowIds.length === 0}
              >
                <EntryPointsIcon />
                Add to Pipeline
              </NormalButton>
              <NormalButton
                size="large"
                variant="secondary"
                className={styles.actionButton}
                isDisabled={selectedAiRowIds.length === 0}
                onClick={() => setShownModal("tags")}
              >
                <TagIcon />
                Tag
              </NormalButton>
              {canDeleteCompetitors && (
                <>
                  <NormalButton
                    size="large"
                    variant="secondary"
                    className={styles.actionButton}
                    isDisabled={selectedAiRowIds.length === 0}
                    onClick={() => setIsOpenDeleteConfirmation(true)}
                  >
                    <TrashIcon />
                    Remove
                  </NormalButton>
                  <ConfirmationModal
                    isOpen={isOpenDeleteConfirmation}
                    onClose={() => setIsOpenDeleteConfirmation(false)}
                    onConfirm={removeCompetitors}
                    content={"Are you sure you want to delete selected competitors?"}
                    title={"Delete Competitors"}
                    dangerConfirm
                    confirmButtonText={"Delete"}
                    cancelButtonText={"Cancel"}
                  />
                </>
              )}
              <AddCompaniesTagsModal
                triggerRefetch={refetchCompetitors}
                isOpen={shownModal === "tags"}
                onClose={() => setShownModal(null)}
                ids={selectedAiRowIds}
                currentTags={[]}
              />
              <AddCompaniesToPipelineModal
                triggerRefetch={() => {}}
                isOpen={shownModal === "addToPipeline"}
                onClose={() => {
                  setShownModal(null);
                  setSelectedAiRowIds([]);
                }}
                ids={selectedAiRowIds}
              />
            </div>
          </div>
        )}
        <div className={styles.contentTableBlockInvestor}>
          <TableLabels
            onChange={selectAllAiCompanies}
            value={selectedAiRowIds.length === shownCompetitorsData?.length && selectedAiRowIds.length > 0}
            isFounder={!isInvestor}
          />
          <TableBody
            selected={selectedAiRowIds}
            onChange={handleAiCardChange}
            companies={tableData}
            isLoading={isLoading}
            triggerRefetch={refetchCompetitors}
            isFounder={!isInvestor}
          />
        </div>
      </>
    );
  };

  function aiProvidedDescriptionContent() {
    if (
      dataStatusFromAccess === EAccessDataStatus.FULL_DATA ||
      dataStatusFromAccess === EAccessDataStatus.PROFILE_DATA ||
      dataStatusFromAccess === EAccessDataStatus.NO_FOUNDER ||
      (!!competitors && competitors?.length > 0) ||
      isLoading
    ) {
      if (!!competitors && competitors.length > 0) {
        return (
          <div className={clsx(styles.innoveraBlock, styles.competitorsBlock)}>
            {competitorsContent()}
            {competitors.length > 5 && (
              <NormalButton
                variant="primary"
                size="small"
                color="greyscale100"
                className={styles.seeMoreBtn}
                onClick={() => setIsCompetitorsShown((prev) => !prev)}
              >
                <CaretDownIcon
                  className={clsx(styles.caretDownIcon, isCompetitorsShown && styles.seeMoreBtnIconActive)}
                />
                {isCompetitorsShown ? "Hide" : "Show more"}
              </NormalButton>
            )}
          </div>
        );
      }
      if (isLoading) return <SectionLoader />;
      if (!competitors?.length) {
        return <NoAiDataContainer section="Competitors" name={companyName || "Company"} onAddDataClick={onEdit} />;
      }
    }

    if (dataStatusFromAccess === EAccessDataStatus.NO_ACCESS) {
      return (
        <div className={clsx(styles.innoveraBlock, styles.competitorsBlock)}>
          {summaryMockDescription}

          <div className={styles.innoveraWhiteBlockNoAccess}>
            <NormalButton
              variant="primary"
              size="medium"
              className={styles.requestButton}
              onClick={onConfirmRequestDataAccess}
              isSubmitting={isLoadingRequestAccess}
            >
              <UnlockIcon />
              <Typography variant="label-4" color="greyscale100">
                Request Data Access
              </Typography>
            </NormalButton>
          </div>
        </div>
      );
    }

    if (dataStatusFromAccess === EAccessDataStatus.PENDING_REQUEST) {
      return (
        <div className={clsx(styles.innoveraBlock, styles.competitorsBlock)}>
          {summaryMockDescription}
          <div className={styles.innoveraWhiteBlockNoAccess}>
            <NormalButton variant="primary" size="medium" className={styles.requestButtonSent}>
              <CheckIcon />
              <Typography variant="label-4" color="greyscale100">
                Access Request Sent
              </Typography>
            </NormalButton>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <SectionContentWrapper isByInnovera={false} companyName={companyName} companyLogoSrc={logoUrl}>
      {aiProvidedDescriptionContent()}
    </SectionContentWrapper>
  );
}
